import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Building2, Search, Phone, Mail, FileText, Download, ChevronDown, ChevronUp, MapPin, Eye,
  User, CheckCircle, XCircle, Camera, Anchor
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { openBlobInNewTab } from '../utils/fileUtils';

const ResponsableEntreprises = ({ user }) => {
  const [entreprises, setEntreprises] = useState([]);
  const [centreNom, setCentreNom] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEntreprise, setExpandedEntreprise] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [entRes, centreRes] = await Promise.all([
        axios.get('/responsable/entreprises'),
        axios.get('/responsable/mon-centre')
      ]);
      
      // Afficher immédiatement sans enrichissement lourd
      setEntreprises((entRes.data || []).map(e => ({ ...e, documents: [] })));
      setCentreNom(centreRes.data?.nom || '');
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Charger profil + documents uniquement quand on déplie une entreprise
  const loadEntrepriseDetails = async (userId) => {
    try {
      const [profileRes, docsRes, docsEntrepriseRes] = await Promise.all([
        axios.get(`/entreprises/${userId}`).catch(() => ({ data: {} })),
        axios.get(`/documents/${userId}`).catch(() => ({ data: [] })),
        axios.get(`/documents/entreprise/${userId}`).catch(() => ({ data: [] }))
      ]);
      const profile = profileRes.data?.profile || {};
      const allDocs = [...(docsRes.data || []), ...(docsEntrepriseRes.data || [])];
      const uniqueDocs = allDocs.filter((doc, index, self) => 
        index === self.findIndex(d => d.document_id === doc.document_id)
      );
      setEntreprises(prev => prev.map(e => {
        if (e.user_id === userId) {
          return { 
            ...e,
            nom_entreprise: profile.nom || profile.nom_entreprise || e.user_info?.nom || e.nom || '',
            adresse: profile.adresse || e.adresse || '',
            code_postal: profile.code_postal || '',
            ville: profile.ville || '',
            telephone: profile.telephone || e.user_info?.telephone || '',
            email: profile.email || e.user_info?.email || '',
            logo: profile.logo || '',
            siret: profile.siret || '',
            activites: profile.activites || {},
            gerant_nom: profile.gerant_nom || '',
            gerant_prenom: profile.gerant_prenom || '',
            gerant_genre: profile.gerant_genre || '',
            gerant_email: profile.gerant_email || '',
            gerant_telephone: profile.gerant_telephone || '',
            documents: uniqueDocs,
            _loaded: true
          };
        }
        return e;
      }));
    } catch (e) {
      // Silently fail
    }
  };

  // Voir un document
  const viewDocument = async (documentId) => {
    try {
      toast.info('Chargement du document...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/pdf' });
        openBlobInNewTab(blob);
      } else {
        toast.error('Document non disponible');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'ouverture du document');
    }
  };

  // Télécharger un document
  const downloadDocument = async (documentId, filename) => {
    try {
      toast.info('Téléchargement...');
      const response = await axios.get(`/documents/download/${documentId}`);
      const doc = response.data;
      if (doc.file_data) {
        const byteCharacters = atob(doc.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: doc.content_type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename || doc.filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Document téléchargé');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const filteredEntreprises = entreprises.filter(e => {
    const searchStr = `${e.nom_entreprise || ''} ${e.adresse || ''} ${e.email || ''}`.toLowerCase();
    return searchStr.includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="responsable-entreprises">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">Consultation seule</span>
          </div>
          <h1 className="text-3xl font-display font-bold text-[#0F172A]">
            Entreprises - Centre {centreNom}
          </h1>
          <p className="text-slate-600">{entreprises.length} entreprise(s) rattachée(s) aux stagiaires du centre</p>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
        <Input
          placeholder="Rechercher une entreprise..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-4">
        <Building2 className="h-8 w-8 text-orange-600" />
        <div>
          <p className="text-2xl font-bold text-orange-700">{entreprises.length}</p>
          <p className="text-sm text-orange-600">Entreprises dans ce centre</p>
        </div>
      </div>

      {/* Liste */}
      <div className="space-y-4">
        {filteredEntreprises.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <Building2 className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucune entreprise rattachée à ce centre</p>
          </div>
        ) : (
          filteredEntreprises.map(entreprise => {
            const isExpanded = expandedEntreprise === entreprise.user_id;
            const nom = entreprise.nom_entreprise || entreprise.nom || entreprise.user_info?.nom || 'Entreprise';
            const activites = entreprise.activites || {};
            
            return (
              <div
                key={entreprise.user_id}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-lg transition-all"
              >
                {/* En-tête */}
                <div 
                  className="p-4 cursor-pointer hover:bg-slate-50"
                  onClick={() => {
                    const newId = isExpanded ? null : entreprise.user_id;
                    setExpandedEntreprise(newId);
                    if (newId && !entreprise._loaded) loadEntrepriseDetails(newId);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center text-white text-xl font-bold">
                        {entreprise.logo ? (
                          <img src={entreprise.logo} alt={nom} className="w-full h-full object-cover" />
                        ) : (
                          nom.substring(0, 2).toUpperCase()
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg text-[#0F172A]">{nom}</h3>
                        <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                          {entreprise.adresse && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" /> {entreprise.ville || entreprise.adresse}
                            </span>
                          )}
                          {entreprise.telephone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" /> {entreprise.telephone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                        {entreprise.documents?.length || 0} document(s)
                      </span>
                      {isExpanded ? (
                        <ChevronUp className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Détails dépliés */}
                {isExpanded && (
                  <div className="border-t border-slate-200 bg-gradient-to-b from-orange-50 to-white p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      
                      {/* Colonne 1: Profil entreprise */}
                      <div className="bg-white p-5 rounded-xl border border-orange-200 shadow-sm">
                        <h4 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
                          <Building2 className="h-5 w-5" />
                          Profil Entreprise
                        </h4>
                        
                        <div className="flex justify-center mb-4">
                          <div className="w-24 h-24 rounded-xl overflow-hidden bg-orange-100 border-4 border-orange-200">
                            {entreprise.logo ? (
                              <img src={entreprise.logo} alt={nom} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-orange-400">
                                <Camera className="h-8 w-8" />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="space-y-3 text-sm">
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Nom</span>
                            <span className="font-medium">{nom}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Adresse</span>
                            <span className="font-medium text-right text-xs">{entreprise.adresse || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Code postal</span>
                            <span className="font-medium">{entreprise.code_postal || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Ville</span>
                            <span className="font-medium">{entreprise.ville || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Téléphone</span>
                            <span className="font-medium">{entreprise.telephone || '-'}</span>
                          </div>
                          <div className="flex justify-between py-2 border-b border-slate-100">
                            <span className="text-slate-500">Email</span>
                            <span className="font-medium text-xs">{entreprise.email || '-'}</span>
                          </div>
                          {entreprise.siret && (
                            <div className="flex justify-between py-2 border-b border-slate-100">
                              <span className="text-slate-500">SIRET</span>
                              <span className="font-medium">{entreprise.siret}</span>
                            </div>
                          )}
                        </div>

                        {(entreprise.gerant_nom || entreprise.gerant_prenom) && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <h5 className="font-medium text-slate-700 mb-2 flex items-center gap-2">
                              <User className="h-4 w-4" />
                              Gérant
                            </h5>
                            <div className="space-y-2 text-sm">
                              <p><span className="text-slate-500">Nom:</span> {entreprise.gerant_genre || ''} {entreprise.gerant_prenom || ''} {entreprise.gerant_nom || '-'}</p>
                              <p><span className="text-slate-500">Email:</span> {entreprise.gerant_email || '-'}</p>
                              <p><span className="text-slate-500">Tél:</span> {entreprise.gerant_telephone || '-'}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Colonne 2: Activités */}
                      <div className="bg-white p-5 rounded-xl border border-orange-200 shadow-sm">
                        <h4 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
                          <Anchor className="h-5 w-5" />
                          Activités Nautiques
                        </h4>
                        
                        <div className="space-y-3">
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm">Randonnée VNM</span>
                            {activites.randonnee_vnm ? (
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-5 w-5 text-green-500" />
                                {activites.nombre_vnm > 0 && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                    {activites.nombre_vnm} VNM
                                  </span>
                                )}
                              </div>
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm">Engins tractés</span>
                            {activites.engins_tractes ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm">PNM</span>
                            {activites.pnm ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm">Flyboard</span>
                            {activites.flyboard ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm">Location bateau</span>
                            {activites.location_bateau ? (
                              <CheckCircle className="h-5 w-5 text-green-500" />
                            ) : (
                              <XCircle className="h-5 w-5 text-slate-300" />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Colonne 3: Documents */}
                      <div className="bg-white p-5 rounded-xl border border-orange-200 shadow-sm">
                        <h4 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          Documents ({entreprise.documents?.length || 0})
                        </h4>
                        {(!entreprise.documents || entreprise.documents.length === 0) ? (
                          <div className="text-center py-8 bg-slate-50 rounded-lg">
                            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                            <p className="text-slate-500 text-sm">Aucun document uploadé</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {entreprise.documents.map((doc, idx) => (
                              <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-orange-600" />
                                  <span className="text-sm font-medium text-orange-800 truncate max-w-[120px]">
                                    {doc.document_type || doc.type || 'Document'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() => viewDocument(doc.document_id)}
                                    className="p-2 text-blue-500 hover:bg-blue-100 rounded-lg transition-colors"
                                    title="Voir"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'doc'}_${nom}`)}
                                    className="p-2 text-green-500 hover:bg-green-100 rounded-lg transition-colors"
                                    title="Télécharger"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ResponsableEntreprises;
